/**
 * Level utility functions for badges and display names
 */

export const getLevelBadgeColor = (level: string | number): string => {
  const levelNum = parseLevelNumber(level);

  switch (levelNum) {
    case 0:
      return "badge-info";
    case 1:
      return "badge-primary";
    case 2:
      return "badge-success";
    case 3:
      return "badge-warning";
    case 4:
      return "badge-teal";
    case 5:
      return "badge-success-green";
    case 6:
      return "badge-primary-border";
    case 7:
      return "badge-custom";
    default:
      return "badge-info";
  }
};

const CANONICAL_LEVEL_NAMES: Record<number, string> = {
  0: "New",
  1: "Acquainted",
  2: "Reactive",
  3: "Proactive",
  4: "Referenceable",
  5: "Strategic",
};

export const getCanonicalLevelName = (
  level: string | number
): string | undefined => CANONICAL_LEVEL_NAMES[parseLevelNumber(level)];

const resolveLevelName = (
  levelNum: number,
  nameOverride?: string | null
): string | undefined =>
  nameOverride?.trim() || CANONICAL_LEVEL_NAMES[levelNum];

/** Short form for chips / table cells, e.g. "AL 3". */
export const getLevelCompactName = (level: string | number): string => {
  const levelNum = parseLevelNumber(level);
  return `AL ${levelNum}`;
};

/** Compact form for dropdowns / badges, e.g. "AL - 3 Proactive". */
export const getAccountLevelCompactLabel = (
  level: string | number,
  nameOverride?: string | null
): string => {
  const levelNum = parseLevelNumber(level);
  const name = resolveLevelName(levelNum, nameOverride);
  return name ? `AL - ${levelNum} ${name}` : `AL - ${levelNum}`;
};

/** Verbose form for detail panels, e.g. "Account Level - 3 Proactive". */
export const getAccountLevelVerboseLabel = (
  level: string | number,
  nameOverride?: string | null
): string => {
  const levelNum = parseLevelNumber(level);
  const name = resolveLevelName(levelNum, nameOverride);
  return name
    ? `Account Level - ${levelNum} ${name}`
    : `Account Level - ${levelNum}`;
};

export const getLevelDisplayName = (level: string | number): string =>
  getAccountLevelVerboseLabel(level);

/** Stored / API level code, always "L{n}". */
export const toStoredLevelCode = (level: string | number): string =>
  `L${parseLevelNumber(level)}`;

/** Extract the level name from any legacy / AL label ("Level 3 - Proactive" → "Proactive"). */
export const extractLevelName = (label?: string | null): string | undefined => {
  if (!label) return undefined;
  const name = label
    .trim()
    .replace(
      /^(account\s*level|level|al|l)\s*[-–—]?\s*-?\d+\s*(?:[-–—:]\s*)?/i,
      ""
    )
    .trim();
  return name || undefined;
};

/** Normalize any legacy / AL string to the compact label ("AL - 3 Proactive"). */
export const toCompactLevelLabel = (fullLabel?: string | null): string => {
  if (!fullLabel) return "";
  return getAccountLevelCompactLabel(fullLabel, extractLevelName(fullLabel));
};

/** Normalize any legacy / AL string to the verbose label. */
export const toVerboseLevelLabel = (fullLabel?: string | null): string => {
  if (!fullLabel) return "";
  return getAccountLevelVerboseLabel(fullLabel, extractLevelName(fullLabel));
};

export const parseLevelNumber = (level: string | number): number => {
  if (typeof level === "number") {
    return Number.isFinite(level) ? level : 0;
  }

  if (typeof level === "string") {
    const trimmed = level.trim();
    if (!trimmed) return 0;

    // Order matters: "Account Level" → "Level" → "AL" → "L#"
    // ("Level …" and "L#" both start with "L").
    if (/^account\s*level\b/i.test(trimmed)) {
      const match = trimmed.match(/^account\s*level\D*?(-?\d+)/i);
      return match ? Number(match[1]) : 0;
    }

    if (/^level\b/i.test(trimmed)) {
      const match = trimmed.match(/level\s*(-?\d+)/i);
      return match ? Number(match[1]) : 0;
    }

    if (/^al\s*-?\s*\d/i.test(trimmed)) {
      const match = trimmed.match(/^al\s*-?\s*(\d+)/i);
      return match ? Number(match[1]) : 0;
    }

    // "L5", "L5 - Strategic", "l0 - New"
    if (/^l\d/i.test(trimmed)) {
      const match = trimmed.match(/^l(-?\d+)/i);
      return match ? Number(match[1]) : 0;
    }

    const match = trimmed.match(/-?\d+/);
    return match ? Number(match[0]) : 0;
  }

  return 0;
};

export const getLevelBadgeClasses = (level?: string | null): string => {
  const levelNum = parseLevelNumber(level ?? "");
  switch (levelNum) {
    case 0:
      return "bg-blue-50 text-blue-700 border border-blue-200";
    case 1:
      return "bg-rose-50 text-rose-700 border border-rose-200";
    case 2:
      return "bg-orange-50 text-orange-700 border border-orange-200";
    case 3:
      return "bg-amber-50 text-amber-800 border border-amber-200";
    case 4:
      return "bg-teal-50 text-teal-700 border border-teal-200";
    case 5:
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    default:
      return "bg-gray-50 text-gray-600 border border-gray-200";
  }
};

export const getAssessmentStatusClasses = (status?: string | null): string => {
  const normalized = (status ?? "").trim().toLowerCase();
  if (
    !normalized ||
    normalized === "never assessed" ||
    normalized === "not assessed" ||
    normalized === "not published" ||
    normalized.startsWith("unassessed")
  ) {
    return "bg-gray-100 text-gray-600 border border-gray-200";
  }
  if (normalized.startsWith("current")) {
    return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  }
  if (normalized.startsWith("stale")) {
    return "bg-amber-50 text-amber-800 border border-amber-200";
  }
  return "bg-gray-50 text-gray-700 border border-gray-200";
};
